import { Diagnostic } from "@siteimprove/alfa-act";
import { Array } from "@siteimprove/alfa-array";
import { Map } from "@siteimprove/alfa-map";
import { Context } from "@siteimprove/alfa-selector";
import { Sequence } from "@siteimprove/alfa-sequence";
import { Style } from "@siteimprove/alfa-style";
import { Serialise } from "./serialise.js";
/**
 * @public
 */
export class ElementDistinguishable {
    static of(distinguishingProperties = [], style = [], pairings = []) {
        return new ElementDistinguishable(Array.from(distinguishingProperties), Map.from(style), Array.from(pairings));
    }
    _distinguishingProperties;
    _style;
    _pairings;
    constructor(distinguishingProperties, style, pairings) {
        this._distinguishingProperties = distinguishingProperties;
        this._style = style;
        this._pairings = pairings;
    }
    get distinguishingProperties() {
        return this._distinguishingProperties;
    }
    get style() {
        return this._style;
    }
    get pairings() {
        return this._pairings;
    }
    withDistinguishingProperties(distinguishingProperties) {
        return ElementDistinguishable.of([...this._distinguishingProperties, ...distinguishingProperties], this._style, this._pairings);
    }
    withStyle(...styles) {
        return ElementDistinguishable.of(this._distinguishingProperties, [...this._style, ...styles], this._pairings);
    }
    withPairings(pairings) {
        return ElementDistinguishable.of(this._distinguishingProperties, this._style, pairings);
    }
    equals(value) {
        return (value instanceof ElementDistinguishable &&
            value._style.equals(this._style) &&
            Array.equals(value._pairings, this._pairings) &&
            Array.equals(value._distinguishingProperties, this._distinguishingProperties));
    }
    hash(hash) {
        Array.hash(this._distinguishingProperties, hash);
        this._style.hash(hash);
        Array.hash(this._pairings, hash);
    }
    toJSON() {
        return {
            distinguishingProperties: Array.toJSON(this._distinguishingProperties),
            style: this._style.toJSON(),
            pairings: Array.toJSON(this._pairings),
        };
    }
}
/**
 * @public
 */
(function (ElementDistinguishable) {
    function from(element, device, target, context = Context.empty(), distinguishingProperties, pairings) {
        const style = Style.from(element, device, context);
        const border = ["color", "style", "width"].map((property) => Serialise.borderShorthand(style, property));
        const cursor = context.isHovered(target)
            ? [["cursor", Serialise.getLonghand(style, "cursor")]]
            : [];
        return ElementDistinguishable.of(distinguishingProperties, [
            ...border,
            ...cursor,
            ["color", Serialise.getLonghand(style, "color")],
            ["font", Serialise.font(style)],
            [
                "vertical-align",
                Serialise.getLonghand(style, "vertical-align"),
            ],
            ["background", Serialise.background(style)],
            ["outline", Serialise.outline(style)],
            ["text-decoration", Serialise.textDecoration(style)],
            ["box-shadow", Serialise.boxShadow(style)],
        ].filter(([_, value]) => value !== ""), Array.sort(Array.from(pairings)));
    }
    ElementDistinguishable.from = from;
})(ElementDistinguishable || (ElementDistinguishable = {}));
/**
 * @public
 */
export class DistinguishingStyles extends Diagnostic {
    static of(message, defaultStyles = Sequence.empty(), hoverStyles = Sequence.empty(), focusStyles = Sequence.empty()) {
        return new DistinguishingStyles(message, Sequence.from(defaultStyles), Sequence.from(hoverStyles), Sequence.from(focusStyles));
    }
    _defaultStyles;
    _hoverStyles;
    _focusStyles;
    constructor(message, defaultStyles, hoverStyles, focusStyles) {
        super(message);
        this._defaultStyles = defaultStyles;
        this._hoverStyles = hoverStyles;
        this._focusStyles = focusStyles;
    }
    get defaultStyles() {
        return this._defaultStyles;
    }
    get hoverStyles() {
        return this._hoverStyles;
    }
    get focusStyles() {
        return this._focusStyles;
    }
    equals(value) {
        return (value instanceof DistinguishingStyles &&
            value._defaultStyles.equals(this._defaultStyles) &&
            value._hoverStyles.equals(this._hoverStyles) &&
            value._focusStyles.equals(this._focusStyles));
    }
    toJSON(options) {
        return {
            ...super.toJSON(options),
            defaultStyle: this._defaultStyles.toJSON(),
            hoverStyle: this._hoverStyles.toJSON(),
            focusStyle: this._focusStyles.toJSON(),
        };
    }
}
/**
 * @public
 */
(function (DistinguishingStyles) {
    /**@public */
    function isDistinguishingStyles(value) {
        return value instanceof DistinguishingStyles;
    }
    DistinguishingStyles.isDistinguishingStyles = isDistinguishingStyles;
})(DistinguishingStyles || (DistinguishingStyles = {}));
//# sourceMappingURL=diagnostics.js.map